import { useQuery } from '@tanstack/react-query';
import Button from '@ui-kit/components/Button';
import { ModalActions } from '@ui-kit/components/Modal/Modal';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DeviceMobileMessageIcon from '@/components/icons/DeviceMobileMessageIcon';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import useRequestMobileVerificationCode from '@/hooks/useRequestMobileVerificationCode';
import { meQueryOptions } from '@/queries/meQuery/meQuery';
import { Input } from '@/ui-kit/components/Input/Input';
import { isValidSeMobile, resolveMobileFormValue, validateSeMobileField } from '@/utils/SE/mobile';

type FormValues = { mobile: string };

const MobileVerification = ({ closeModal, confirmAction = closeModal }: ModalProps) => {
  const { t } = useTranslation(['Modal', 'fieldErrors', 'Common']);

  const { data: me, isPending: isMeLoading } = useQuery(meQueryOptions());

  const { register, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: { mobile: resolveMobileFormValue() },
    values: me && isValidSeMobile(me.mobile) ? { mobile: resolveMobileFormValue(me.mobile) } : undefined,
    resetOptions: { keepDirtyValues: true },
  });
  const { errors } = formState;

  const handleConfirm = () => {
    closeModal();
    confirmAction();
  };

  const { requestMobileVerificationCode, isPending, errorMessage } = useRequestMobileVerificationCode({
    onSuccess: handleConfirm,
    onAlreadyVerified: handleConfirm,
  });

  return (
    // TODO: Revisit empty Modal title + custom heading layout (shared with EmailVerification).
    <Modal title="">
      <div className="-mt-2 mb-6 flex flex-col items-center text-center">
        <DeviceMobileMessageIcon className="text-icon-brand mb-4 size-12" aria-hidden="true" />
        <h2 className="text-heading mb-2 lg:text-center">{t('Modal:MobileVerification.title')}</h2>
        <p className="text-text-subtle">{t('Modal:MobileVerification.subtitle')}</p>
      </div>
      <form
        className="flex flex-col gap-4"
        onSubmit={e => handleSubmit(({ mobile }) => requestMobileVerificationCode(mobile))(e)}
      >
        <Input
          data-private
          {...register('mobile', {
            required: `${t('Common:requiredField')}`,
            validate: value =>
              validateSeMobileField(value, {
                invalid: t('fieldErrors:invalidMobile'),
                tooLong: length => t('fieldErrors:mobileMaxLengthError', { length }),
              }),
          })}
          type="tel"
          label={t('Modal:MobileVerification.mobileLabel')}
          disabled={isPending || isMeLoading}
          errorMessage={errorMessage ?? errors.mobile?.message}
          enterKeyHint="go"
        />
        <ModalActions>
          <Button type="submit" loading={isPending}>
            {t('Modal:MobileVerification.submit')}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
};

export default MobileVerification;
